import { http } from '../api/http';
import { ENV } from '../config/env';

export type ReportType = 'statement' | 'invoice' | 'performance' | 'generic';

export interface ReportItem {
  id: number | string;
  type: ReportType;
  title?: string | null;
  date: string;        // ISO o display string
  fileUrl: string;     // URL directa o firmada
}

export interface DownloadReportResult {
  blob: Blob;
  filename: string;
  mime: string;
}

export interface DownloadReportParams {
  type?: ReportType;
  format?: 'pdf' | 'csv';
  month?: string;
  from?: string;
  to?: string;
  developmentId?: number;
}

function getToken() {
  return localStorage.getItem('auth_token');
}

function deriveFilenameFromHeaders(headers: Headers, fallback: string) {
  const cd = headers.get('content-disposition') || headers.get('Content-Disposition');
  if (!cd) return fallback;

  const match = cd.match(/filename\*?=(?:UTF-8'')?("?)([^";]+)\1/i);
  if (!match?.[2]) return fallback;

  try {
    return decodeURIComponent(match[2]);
  } catch {
    return match[2];
  }
}

export const ReportsService = {
  /**
   * GET /reports/me
   * Espera: ReportItem[]
   */
  getMyReports() {
    return http<ReportItem[]>('/reports/me');
  },

  /**
   * POST /reports/generate/statement
   * Genera un nuevo estado de cuenta
   */
  generateStatement() {
    return http<ReportItem>('/reports/generate/statement', {
      method: 'POST'
    });
  },

  /**
   * POST /reports/generate/performance
   * Genera un nuevo reporte de rendimiento
   */
  generatePerformanceReport() {
    return http<ReportItem>('/reports/generate/performance', {
      method: 'POST'
    });
  },

  /**
   * GET /reports/download/:id
   * Descarga un reporte específico
   */
  downloadReport(reportId: number | string) {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    return `${baseUrl}/reports/download/${reportId}`;
  },

  async downloadReportFile(
    reportId: number | string,
    params: DownloadReportParams = {},
  ): Promise<DownloadReportResult> {
    const sp = new URLSearchParams();
    if (params.type) sp.set('type', params.type);
    if (params.format === 'csv') sp.set('format', 'csv');
    if (params.month) sp.set('month', params.month);
    if (params.from) sp.set('from', params.from);
    if (params.to) sp.set('to', params.to);
    if (typeof params.developmentId === 'number') sp.set('developmentId', String(params.developmentId));

    const endpoint = `/reports/download/${reportId}${sp.toString() ? `?${sp.toString()}` : ''}`;
    const url = `${ENV.API_URL}${endpoint}`;
    const token = getToken();
    const expectedFormat = params.format === 'csv' ? 'csv' : 'pdf';

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: expectedFormat === 'csv' ? 'text/csv' : 'application/pdf',
      },
    });

    if (!res.ok) {
      const contentType = res.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      const payload = isJson ? await res.json().catch(() => null) : await res.text().catch(() => '');
      const message =
        (typeof payload === 'string' && payload.trim()) ||
        (payload && typeof payload.message === 'string' ? payload.message : '') ||
        'Request failed';
      throw new Error(message);
    }

    const blob = await res.blob();
    const mime = res.headers.get('content-type') || (expectedFormat === 'csv' ? 'text/csv' : 'application/pdf');
    const filename = deriveFilenameFromHeaders(
      res.headers,
      `reporte-${reportId}.${expectedFormat === 'csv' ? 'csv' : 'pdf'}`,
    );
    return { blob, filename, mime };
  },
};
